import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface CalendarWidgetProps {
  domain: string;
}

interface Holiday {
  id: string;
  title: string;
  date: string;
  type: 'public' | 'school' | 'optional';
  description: string;
  year: string;
  month: string;
  day: string;
}

const CalendarWidget: React.FC<CalendarWidgetProps> = ({ domain }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHolidays();
  }, [domain, currentDate]);

  const loadHolidays = async () => {
    if (!domain) return;
    try {
      const holidaysRef = collection(db, `school-data/${domain}/holidays`);
      const snapshot = await getDocs(holidaysRef);

      const holidaysList: Holiday[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as Holiday));

      const currentYear = currentDate.getFullYear().toString();
      const currentMonth = currentDate.toLocaleString('default', { month: 'long' });

      // Filter for current month and year
      const filtered = holidaysList.filter(
        h => h.year === currentYear && h.month === currentMonth
      );

      setHolidays(filtered);
    } catch (error) {
      console.error('Error loading holidays:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const getHolidayForDay = (day: number) => {
    return holidays.find(h => parseInt(h.day) === day);
  };

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const monthName = currentDate.toLocaleString('default', { month: 'long' });
  const year = currentDate.getFullYear();

  // Create array of days for the calendar
  const calendarDays: (number | null)[] = [];

  // Add empty cells for days before the first day of month
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }

  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  return (
    <div className="card shadow-sm h-100">
      <div className="card-header bg-primary-custom text-white">
        <div className="d-flex justify-content-between align-items-center">
          <button
            className="btn btn-sm btn-light"
            onClick={previousMonth}
          >
            <i className="bi bi-chevron-left"></i>
          </button>
          <h6 className="mb-0">
            <i className="bi bi-calendar3 me-2"></i>
            {monthName} {year}
          </h6>
          <button
            className="btn btn-sm btn-light"
            onClick={nextMonth}
          >
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>

      <div className="card-body p-2">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Calendar Grid */}
            <div className="table-responsive">
              <table className="table table-bordered mb-0" style={{ fontSize: '0.85rem' }}>
                <thead className="table-light">
                  <tr>
                    <th className="text-center p-1">Sun</th>
                    <th className="text-center p-1">Mon</th>
                    <th className="text-center p-1">Tue</th>
                    <th className="text-center p-1">Wed</th>
                    <th className="text-center p-1">Thu</th>
                    <th className="text-center p-1">Fri</th>
                    <th className="text-center p-1">Sat</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: Math.ceil(calendarDays.length / 7) }).map((_, weekIndex) => (
                    <tr key={weekIndex}>
                      {Array.from({ length: 7 }).map((_, dayIndex) => {
                        const cellIndex = weekIndex * 7 + dayIndex;
                        const day = calendarDays[cellIndex];
                        const holiday = day ? getHolidayForDay(day) : null;

                        return (
                          <td
                            key={dayIndex}
                            className={`text-center p-1 ${
                              day
                                ? isToday(day)
                                  ? 'bg-primary text-white fw-bold'
                                  : holiday
                                  ? holiday.type === 'public'
                                    ? 'bg-danger text-white'
                                    : holiday.type === 'school'
                                    ? 'bg-warning bg-opacity-25'
                                    : 'bg-info bg-opacity-25'
                                  : ''
                                : 'bg-light'
                            }`}
                            style={{
                              height: '45px',
                              verticalAlign: 'middle',
                              cursor: holiday ? 'pointer' : 'default'
                            }}
                            title={holiday ? `${holiday.title} (${holiday.type})` : ''}
                          >
                            {day && (
                              <div className="d-flex flex-column align-items-center justify-content-center">
                                <span>{day}</span>
                                {holiday && (
                                  <i className="bi bi-star-fill" style={{ fontSize: '0.6rem' }}></i>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Holiday Legend */}
            <div className="mt-3 d-flex flex-wrap gap-2 justify-content-center" style={{ fontSize: '0.75rem' }}>
              <div className="d-flex align-items-center gap-1">
                <div className="bg-primary" style={{ width: '15px', height: '15px', borderRadius: '3px' }}></div>
                <span>Today</span>
              </div>
              <div className="d-flex align-items-center gap-1">
                <div className="bg-danger" style={{ width: '15px', height: '15px', borderRadius: '3px' }}></div>
                <span>Public Holiday</span>
              </div>
              <div className="d-flex align-items-center gap-1">
                <div className="bg-warning bg-opacity-25 border" style={{ width: '15px', height: '15px', borderRadius: '3px' }}></div>
                <span>School Holiday</span>
              </div>
              <div className="d-flex align-items-center gap-1">
                <div className="bg-info bg-opacity-25 border" style={{ width: '15px', height: '15px', borderRadius: '3px' }}></div>
                <span>Optional</span>
              </div>
            </div>

            {/* Upcoming Holidays */}
            {holidays.length > 0 && (
              <div className="mt-3">
                <h6 className="text-muted small mb-2">
                  <i className="bi bi-calendar-event me-1"></i>
                  This Month's Holidays
                </h6>
                <div className="list-group list-group-flush">
                  {holidays.slice(0, 3).map((holiday) => (
                    <div key={holiday.id} className="list-group-item p-2 border-0">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <small className="fw-bold text-primary me-2">{holiday.day}</small>
                          <small className="fw-bold">{holiday.title}</small>
                          <br />
                          <small className="text-muted">
                            {new Date(holiday.date).toLocaleDateString('en-IN', { weekday: 'short' })}
                          </small>
                        </div>
                        <span className={`badge ${
                          holiday.type === 'public' ? 'bg-danger' :
                          holiday.type === 'school' ? 'bg-warning' : 'bg-info'
                        }`} style={{ fontSize: '0.65rem' }}>
                          {holiday.type}
                        </span>
                      </div>
                    </div>
                  ))}
                  {holidays.length > 3 && (
                    <div className="text-center mt-2">
                      <small className="text-muted">+{holidays.length - 3} more</small>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CalendarWidget;
